import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { MdKeyboardArrowRight } from 'react-icons/md';
import MuiAccordion, {
  AccordionProps as AccordionItemProps,
} from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';

const AccordionItem = styled((props: AccordionItemProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<MdKeyboardArrowRight />} {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : '#fafafa',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

type AccordionItemData = {
  key: string;
  title: string | JSX.Element;
  body: string | JSX.Element;
};

export type Expanded = string | false | undefined;

interface AccordionProps {
  data: AccordionItemData[];
  openLastItemOnAdd?: boolean;
  openItemIndex?: number | false;
  onItemOpen?: (itemIndex: number | false) => void;
}

export const Accordion = ({
  data,
  openLastItemOnAdd = false,
  openItemIndex,
  onItemOpen,
}: AccordionProps) => {
  const [expanded, setExpanded] = useState<Expanded>(
    openItemIndex !== undefined && openItemIndex !== false
      ? data[openItemIndex]?.key
      : false
  );
  const [originalData, setOriginalData] = useState<AccordionItemData[]>(data);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      const expanded = newExpanded ? panel : false;

      setExpanded(expanded);

      onItemOpen !== undefined &&
        onItemOpen(
          expanded ? data.findIndex((d) => d.key === expanded) : false
        );
    };

  useEffect(() => {
    if (
      openLastItemOnAdd &&
      (originalData.length < data.length ||
        (originalData.length === 1 && data.length === 1))
    ) {
      setExpanded(data[data.length - 1].key);

      setOriginalData(data);
    }
  }, [openLastItemOnAdd, data.length, originalData.length]);

  useEffect(() => {
    if (openItemIndex !== undefined && openItemIndex !== false) {
      setExpanded(data[openItemIndex]?.key);
    }
  }, [openItemIndex, data]);

  const renderData = () => {
    return data.map((d) => {
      return (
        <AccordionItem
          key={d.key}
          expanded={expanded === d.key}
          onChange={handleChange(d.key)}
        >
          <AccordionSummary
            aria-controls={`${d.key}d-content`}
            id={`${d.key}d-header`}
          >
            {d.title}
          </AccordionSummary>
          <AccordionDetails>{d.body}</AccordionDetails>
        </AccordionItem>
      );
    });
  };

  return (
    <div
      style={{
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid rgba(0, 0, 0, .125)',
      }}
    >
      {renderData()}
    </div>
  );
};
